import {ButtonGroup, Card, DataStatus, IconButton, openDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {isNil} from 'ramda';

import {SeriesType, useGetBranchListQuery, useGetSeriesListQuery} from '@dms/api/core';
import {
  useGetWorkshopInterestSeriesDefinitionListQuery,
  WorkshopInterestSeriesDefinitionResponseBody,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {SimpleTable} from '@dms/teas';

import {WorkshopInterestSeriesDefinitionForm} from './WorkshopInterestSeriesDefinitionForm';

const WORKSHOP_INTEREST_SERIES_TYPE: SeriesType = 'workshop/workshop-interest';
const ACTIONS_COLUMN_WIDTH = 100;

export function WorkshopInterestSeriesDefinitionList() {
  const {
    data: definitions = [],
    isLoading: isLoadingDefinitions,
    isError: isErrorDefinitions,
  } = useGetWorkshopInterestSeriesDefinitionListQuery();

  const {data: branches} = useGetBranchListQuery();
  const {data: series} = useGetSeriesListQuery({
    type: [WORKSHOP_INTEREST_SERIES_TYPE],
  });

  const handleOpenEdit = (definitionId: WorkshopInterestSeriesDefinitionResponseBody['id']) => {
    openDialog(<WorkshopInterestSeriesDefinitionForm definitionId={definitionId} />, {
      title: i18n.t('page.workshopInterestSeriesDefinition.actions.editDefinition'),
    });
  };

  const handleCreateNew = () => {
    openDialog(<WorkshopInterestSeriesDefinitionForm />, {
      title: i18n.t('page.workshopInterestSeriesDefinition.actions.newDefinition'),
    });
  };

  const columns = [
    {
      Header: i18n.t('entity.seriesDefinition.labels.definitionName'),
      id: 'name',
    },
    {
      Header: i18n.t('entity.branch.labels.branch'),
      id: 'branch',
      Cell: (cellProps: {row: WorkshopInterestSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        if (isNil(row.branchId)) {
          return i18n.t('general.labels.all');
        }

        return (
          branches?.branchListItems.find((branch) => branch.id === row.branchId)?.marketingName ??
          row.branchId
        );
      },
    },
    {
      Header: i18n.t('entity.seriesDefinition.labels.interestDefinitionSeries'),
      id: 'definitionSeries',
      Cell: (cellProps: {row: WorkshopInterestSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        return series?.find((item) => item.id === row.seriesId)?.name ?? row.seriesId;
      },
    },
    {
      Header: '',
      id: 'actions',
      width: ACTIONS_COLUMN_WIDTH,
      Cell: (cellProps: {row: WorkshopInterestSeriesDefinitionResponseBody}) => {
        const row = cellProps.row;

        return (
          <Box paddingHorizontal={3}>
            <ButtonGroup align="right">
              <IconButton
                key="edit"
                size="small"
                icon="image/edit"
                data-testid="workshopInterestSeriesDefinitionListEdit"
                onClick={() => handleOpenEdit(row.id)}
              />
            </ButtonGroup>
          </Box>
        );
      },
    },
  ];

  return (
    <Card
      title={i18n.t('page.workshopInterestSeriesDefinition.labels.definitions')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'content/add_circle',
          onClick: handleCreateNew,
          title: i18n.t('page.workshopInterestSeriesDefinition.actions.newDefinition'),
        },
      ]}
    >
      <DataStatus isLoading={isLoadingDefinitions} isError={isErrorDefinitions} spacing={5}>
        <SimpleTable
          noZebra
          showRowDivider
          tdPadding="default"
          highlightRowOnHover
          rows={[
            {
              data: definitions,
            },
          ]}
          getRowId={(row) => row.id}
          columns={columns}
        />
      </DataStatus>
    </Card>
  );
}
